import { createReducer } from 'typesafe-actions';
import { WidgetAction } from '@store/actions';
import {
    broadcastCashOutResponse,
    clearLoanNDStatus,
    fetchWidgetAsync,
    fetchWidgetRefreshAsync,
    setOfferTypeSelected,
    setSelectedLoanNumber,
    updateWidgetOfferCards,
    updateNbpCardsRefactor,
    removeTileOffer,
    updatePropertyValue,
    updatePropertyValueError,
    removeHVtileChildProduct
} from '@store/actions/widgets.actions';
import { Identifier, Widgets, WidgetTemplate } from '@types';
import { get, groupBy } from 'lodash';
import { CodingConstants } from '@constants';

type DashboardLoanType = 'ServicingLoanNumber' | 'OriginationLoanNumber';

export interface WidgetState {
    widgets: Widgets;
    metadata: any;
    identifier: Identifier | undefined;
    offerTypeSelected?: string;
    clearedLoansFromND?: string[];
    dashboardType: DashboardLoanType;
    nbpCard: any;
    propertyValue: any;
    propertyValueError: boolean;
}

export const widgets: WidgetState = {
    widgets: {} as Widgets,
    metadata: {},
    identifier: undefined,
    dashboardType: undefined,
    clearedLoansFromND: [],
    nbpCard: [],
    propertyValue: undefined,
    propertyValueError: false
};

export const widgetReducer = createReducer<WidgetState, WidgetAction>(widgets)
    .handleAction(fetchWidgetAsync.success, (state, action) => ({
        ...state,
        widgets: action.payload,
        nbpCard: normalizeNbpCard(state, action.payload),
        dashboardType: get(action.payload, ['profile', 'identifiers', '0', 'type'], undefined)
    }))
    .handleAction(broadcastCashOutResponse, (state, action) => ({ ...state, metadata: action.payload }))
    .handleAction(setSelectedLoanNumber, (state, action) => {
        const loanList = get(state, ['widgets', 'profile', 'identifiers']) || [];
        const loanIds = loanList.map((loan: Identifier) => loan.identifier);
        const isLoanInList = loanIds.includes(action.payload?.identifier);
        return { ...state, identifier: isLoanInList ? action.payload : loanList[0] };
    })
    .handleAction(setOfferTypeSelected, (state, action) => ({ ...state, offerTypeSelected: action.payload }))
    .handleAction(updateWidgetOfferCards, (state, action) => ({ ...state, payload: action.payload }))

    .handleAction(updateNbpCardsRefactor, (state, action) => ({
        ...state,
        nbpCard: action.payload
    }))
    .handleAction(clearLoanNDStatus, (state, action) => ({
        ...state,
        clearedLoansFromND: [...state.clearedLoansFromND, action.payload]
    }))
    .handleAction(removeTileOffer, (state, action) => removeOffer(state, action.payload))
    .handleAction(removeHVtileChildProduct, (state, action) => removeChildProduct(state, action.payload))
    .handleAction(updatePropertyValue, (state, action) => ({
        ...state,
        propertyValue: action.payload
    }))
    .handleAction(updatePropertyValueError, (state, action) => ({
        ...state,
        propertyValueError: action.payload
    }));

const normalizeNbpCard = (state: WidgetState, widgets: Widgets | undefined) => {
    const navigateToDeepLink = localStorage.getItem('launchpad');
    if (Array.isArray(widgets?.cards) && widgets?.cards.length) {
        const nbpCards = widgets?.cards.find((card: any) => card.id === 'next-best-popup');
        if (nbpCards && Array.isArray(nbpCards.initParam) && nbpCards.initParam.length) {
            const initParams = nbpCards.initParam;

            const createPopupObject = (card: any, popup: any, state: WidgetState) => ({
                loanNumber: card.loanNumber,
                id: popup.product,
                portCode: card?.portCode,
                status: isNBPstatusCompleted(state, { id: popup.product, loanNumber: card.loanNumber }) ? 'completed' : 'pending',
                ...popup
            });

            const updatedNBPCard = initParams.flatMap((card: any) =>
                card?.popups.map((popup: any) => {
                    if (navigateToDeepLink) {
                        if (CodingConstants.deepLinkPopupFilters.includes(popup.product)) {
                            return createPopupObject(card, popup, state);
                        }
                    } else {
                        return createPopupObject(card, popup, state);
                    }
                })
            );
            const result = groupBy(updatedNBPCard, 'loanNumber');
            return result;
        }
    }
    return { '': [] };
};

const isNBPstatusCompleted = (state: WidgetState, payload: { id: string; loanNumber: string }) => {
    const { nbpCard } = state;
    const { id, loanNumber } = payload; // TODO nbpCard is the main object that you set using normalize method.
    if (nbpCard === undefined || nbpCard[loanNumber] === undefined) return false;
    return nbpCard[loanNumber].find((x: any) => x.id === id)?.status === 'completed';
};

const removeChildProduct = (state: WidgetState, payload: { loanNumber: string; product: string; offerGUID?: string }) => {
    const cards = state?.widgets?.cards;
    if (cards) {
        const hvCard = cards.find(card => card?.id === CodingConstants.homeValueWidgetId);
        const otherCards = cards.filter(card => card?.id !== CodingConstants.homeValueWidgetId);

        if (hvCard) {
            const otherHV = hvCard.initParam.filter(loan => loan?.loanNumber !== payload.loanNumber);
            const selectedHV = hvCard.initParam.find(loan => loan?.loanNumber === payload.loanNumber);
            if (selectedHV?.predefinedCardDetails?.length > 0 && selectedHV?.predefinedCardDetails[0]?.childProduct) {
                const result = {
                    ...state,
                    widgets: {
                        ...state?.widgets,
                        cards: [
                            ...otherCards,
                            {
                                ...hvCard,
                                initParam: [
                                    ...otherHV,
                                    {
                                        ...selectedHV,
                                        predefinedCardDetails: [{ ...selectedHV.predefinedCardDetails[0], childProduct: undefined }]
                                    }
                                ]
                            }
                        ]
                    }
                };
                return result;
            }
        }
    }

    return { ...state };
};

const removeOffer = (state: WidgetState, payload: { loanNumber: string; product: string; offerGUID?: string }) => {
    const cards = state?.widgets?.cards;
    if (cards) {
        const otherCards = cards.filter(card => card?.id !== 'offers');
        const offersCard = cards.find(card => card?.id === 'offers');
        if (offersCard) {
            const otherLoansOffers = offersCard.initParam.filter(loan => loan?.loanNumber !== payload.loanNumber);
            const selectedLoanOffers = offersCard.initParam.find(loan => loan?.loanNumber === payload.loanNumber);
            if (selectedLoanOffers) {
                const filteredOffers = selectedLoanOffers?.offers.filter((offer: any) => offer?.product !== payload.product);
                return {
                    ...state,
                    widgets: {
                        ...state?.widgets,
                        cards: [
                            ...otherCards,
                            {
                                ...offersCard,
                                initParam: [
                                    ...otherLoansOffers,
                                    {
                                        ...selectedLoanOffers,
                                        offers: filteredOffers
                                    }
                                ]
                            }
                        ]
                    }
                };
            }
        }
    }
    return { ...state };
};

export const widgetRefreshReducer = createReducer<WidgetState, WidgetAction>(widgets).handleAction(
    fetchWidgetRefreshAsync.success,
    (state, action) => ({
        ...state,
        widgets: action.payload
    })
);
